import Button from '@dustin-web/microsite-components/src/button'
import { useSiteContext, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import { useFormContext } from 'react-hook-form'
import { StandardRegisterProps } from '.'
import ValidationMessage from 'src/components/validationMessage'
import classNames from 'classnames'
import { useMemo } from 'react'
import { getAccountTermsUrl } from 'src/helperFunctions'
import { GaRegEvent } from 'src/components/gaRegistrationHook/gaRegistrationEventHook'

type Props = {
  isConsumer?: boolean
  onNavigateBack: () => void
}

const Account = ({ isConsumer = false, onNavigateBack }: Props) => {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<StandardRegisterProps>()
  const { translate } = useTranslations()
  const watchIsAuthenticated: boolean = watch('isAuthenticated')
  const watchAcceptAccountTerms: boolean = watch('acceptAccountTerms')
  const watchFirstName: string = watch('firstName')
  const watchLastName: string = watch('lastName')
  const { accountGaEvent } = GaRegEvent()

  const { site } = useSiteContext()
  const accountTermsUrl = getAccountTermsUrl(site)

  const validAccountForm: boolean = useMemo(
    () =>
      watchAcceptAccountTerms &&
      (watchFirstName?.length ?? 0) > 0 &&
      (watchLastName?.length ?? 0) > 0,
    [watchAcceptAccountTerms, watchFirstName, watchLastName]
  )

  const displayBackButton: boolean = useMemo(
    () => !watchIsAuthenticated || !isConsumer,
    [watchIsAuthenticated, isConsumer]
  )

  return (
    <div className="row">
      <div className="col-12 col-lg-6">
        <div className="column u-pb-24">
          <div
            className={classNames('col-12 col-lg-auto c-form__item c-form__item--mandatory u-px-0', {
              'c-form__error': errors.firstName,
            })}>
            <label htmlFor="register-first-name">{translate('Register_FirstName')}</label>
            <input
              id="register-first-name"
              autoComplete="off"
              type="text"
              {...register('firstName')}
              aria-invalid={errors.firstName ? 'true' : 'false'}
              aria-describedby={errors.firstName ? 'firstNameError' : undefined}
            />
            {errors.firstName?.type === 'required' && (
              <ValidationMessage id="firstNameError">
                {translate('Validation_FirstNameIsRequired')}
              </ValidationMessage>
            )}
            {errors.firstName?.type === 'max' && (
              <ValidationMessage id="firstNameError">
                {translate('MaxLength', [translate('Register_FirstName'), '25'])}
              </ValidationMessage>
            )}
          </div>
          <div
            className={classNames('col-12 col-lg-auto c-form__item c-form__item--mandatory  u-px-0', {
              'c-form__error': errors.lastName,
            })}>
            <label htmlFor="register-last-name">{translate('Register_LastName')}</label>
            <input
              id="register-last-name"
              autoComplete="off"
              type="text"
              {...register('lastName')}
              aria-invalid={errors.lastName ? 'true' : 'false'}
              aria-describedby={errors.lastName ? 'lastNameError' : undefined}
            />
            {errors.lastName?.type === 'required' && (
              <ValidationMessage id="lastNameError">
                {translate('Validation_LastNameIsRequired')}
              </ValidationMessage>
            )}
            {errors.lastName?.type === 'max' && (
              <ValidationMessage id="lastNameError">
                {translate('MaxLength', [translate('Register_LastName'), '25'])}
              </ValidationMessage>
            )}
          </div>
        </div>
        <div
          className={classNames('row', {
            'u-pt-8': watchIsAuthenticated,
          })}>
          <div className="col">
            <div className="c-form__item">
              <div className="c-checkbox">
                <input
                  id="register-offers-by-email"
                  type="checkbox"
                  {...register('offersByEmail')}
                />
                <label htmlFor="register-offers-by-email">
                  {translate('Register_OffersByEmail')}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="row u-pb-24">
          <div className="col">
            <div className="c-form__item">
              <div className="c-checkbox">
                <input
                  id="register-accept-account-terms"
                  type="checkbox"
                  {...register('acceptAccountTerms')}
                  aria-invalid={errors.acceptAccountTerms ? 'true' : 'false'}
                  aria-describedby={
                    errors.acceptAccountTerms ? 'acceptAccountTermsError' : undefined
                  }
                />
                <label htmlFor="register-accept-account-terms">
                  {translate('Register_AcceptAccountTerms')}&nbsp;
                  <a href={accountTermsUrl} target="_blank" rel="noreferrer">
                    {translate('Register_AcceptAccountTerms_LinkText')}
                  </a>
                </label>
              </div>
              {errors.acceptAccountTerms?.message && (
                <ValidationMessage id="acceptAccountTermsError">
                  {translate('Validation_AcceptAccountTermsIsRequired')}
                </ValidationMessage>
              )}
            </div>
          </div>
        </div>
        <div className="row d-flex justify-content-lg-end">
          {displayBackButton && (
            <div className="col-6 col-lg-auto u-pr-lg-4 order-lg-first">
              <Button
                className="u-w-100 u-w-lg-auto"
                text={translate('Registration_Back')}
                onClick={onNavigateBack}
              />
            </div>
          )}
          <div className={classNames('col-6 col-lg-auto', { 'u-pl-lg-4': displayBackButton })}>
            <Button
              id="btn-account-confirm-create"
              disabled={!validAccountForm}
              className="u-w-100 u-w-lg-auto"
              primary
              type="submit"
              text={translate('Register_SubmitButton')}
              onClick={() => accountGaEvent()}
            />
          </div>

        </div>
      </div>
    </div>
  )
}

export default Account
